import { ListItemNode } from "@lexical/list"
import { LexicalNode, ElementNode, DOMExportOutput, EditorConfig } from "lexical"

export class DirectionalListItemNode extends ListItemNode {
    __direction: "ltr" | "rtl" | null = null

    static getType(): string {
        return "directional-list-item"
    }

    static clone(node: DirectionalListItemNode): DirectionalListItemNode {
        const clone = new DirectionalListItemNode(node.__value, node.__checked, node.__key)
        clone.__direction = node.__direction
        return clone
    }

    afterCloneFrom(prevNode: ElementNode): void {
        super.afterCloneFrom(prevNode as any)
        if (prevNode instanceof DirectionalListItemNode) {
            this.__direction = prevNode.__direction
        }
    }

    setDirection(direction: "ltr" | "rtl"): this {
        const self = this.getWritable()
        self.__direction = direction
        return this
    }

    getDirection(): "ltr" | "rtl" | null {
        return this.__direction
    }

    exportDOM(): DOMExportOutput {
        const element = document.createElement("li")
        const direction = this.getDirection()
        if (direction) {
            element.dir = direction
            element.style.direction = direction
            element.style.unicodeBidi = "embed"

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                element.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                element.style.textAlign = direction === "rtl" ? "right" : "left"
            }
        }
        return { element }
    }

    createDOM(config: any): HTMLElement {
        const element = super.createDOM(config)
        const direction = this.getDirection()
        if (direction) {
            element.dir = direction
            element.style.direction = direction
            element.style.unicodeBidi = "embed"

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                element.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                element.style.textAlign = direction === "rtl" ? "right" : "left"
            }
        }
        return element
    }

    updateDOM(prevNode: DirectionalListItemNode, dom: HTMLElement, config: EditorConfig): boolean {
        const direction = this.getDirection()
        if (direction !== prevNode.getDirection()) {
            dom.dir = direction || ""
            dom.style.direction = direction || ""
            dom.style.unicodeBidi = "embed"

            // Always respect the format type for text alignment
            const format = this.getFormatType()

            // Apply text alignment based on format
            if (format === "left" || format === "right" || format === "center" || format === "justify") {
                dom.style.textAlign = format
            } else {
                // Only use direction-based alignment as fallback
                dom.style.textAlign = direction === "rtl" ? "right" : "left"
            }
        }
        return super.updateDOM(prevNode, dom, config)
    }
}

export function $createDirectionalListItemNode(direction?: "ltr" | "rtl"): DirectionalListItemNode {
    const node = new DirectionalListItemNode()
    if (direction) {
        node.setDirection(direction)
    }
    return node
}

export function $isDirectionalListItemNode(node: LexicalNode | null | undefined): node is DirectionalListItemNode {
    return node instanceof DirectionalListItemNode
}
